import { useEffect, useState } from 'react';
import { formatPercent, formatRial } from '@kia/shared';
import { api } from '../api/client';
import { Card, Chip, Kpi } from '../ui';

/**
 * تشخیص انحراف جذب سربار — the absorption-skew DIAGNOSTIC (accuracy audit #4). READ-ONLY: it surfaces how the
 * winding-weight base distributes the conversion pool across product types (a live labour-factor lever was
 * red-team-rejected — it would break the frozen cost weld and bake an unmeasured guess into the books). It
 * changes no cost/margin; it lets the owner SEE whether a real slice of sales is mis-absorbed.
 */
interface SkewRow {
  type: string;
  units: number;
  revenue: number;
  revenueShare: number;
  productCount: number;
  avgMaterialPerUnit: number;
  avgConversionPerUnit: number;
  conversionShareOfCogs: number;
  fallbackUnitShare: number;
  flag: 'low' | 'high' | null;
}
interface Skew {
  from: string;
  to: string;
  rate: number;
  bookConversionShare: number;
  medianTypeShare: number;
  rows: SkewRow[];
  note: string;
}

export function AbsorptionSkewCard() {
  const [s, setS] = useState<Skew | null>(null);
  useEffect(() => {
    void api<Skew>('/api/analytics/absorption-skew').then((r) => {
      if (r.ok) setS(r.body as Skew);
    });
  }, []);
  if (!s) return null;

  return (
    <Card title="تشخیص انحراف جذب سربار (اطلاعاتی)">
      <p style={{ color: 'var(--ink-2)', lineHeight: 1.9, marginTop: 0 }}>{s.note}</p>

      {s.rate <= 0 || s.rows.length === 0 ? (
        <p style={{ color: 'var(--ink-3)', margin: 0 }}>
          {s.rate <= 0
            ? 'نرخ جذب صفر است (فرض ماهانه پیکربندی نشده) — انحرافی برای نمایش نیست.'
            : 'در این بازه تحویلی ثبت نشده — بدون تحویل، توزیع جذب قابل سنجش نیست.'}
        </p>
      ) : (
        <>
          <div className="kpi-grid" style={{ marginBottom: 12 }}>
            <Kpi label="نرخ جذب (﷼ بر eqKg)" value={formatRial(s.rate)} />
            <Kpi label="سهم جذب از بها (کل کتاب)" value={formatPercent(s.bookConversionShare)} />
            <Kpi label="میانهٔ سهم نوع‌ها" value={formatPercent(s.medianTypeShare)} />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>نوع محصول</th>
                  <th>سهم فروش</th>
                  <th>مواد/واحد</th>
                  <th>جذب/واحد</th>
                  <th>سهم جذب از بها</th>
                  <th>سهم eqKg تخمینی</th>
                </tr>
              </thead>
              <tbody>
                {s.rows.map((r) => (
                  <tr key={r.type}>
                    <td>
                      {r.type}
                      {r.flag === 'low' ? (
                        <>
                          {' '}
                          <Chip tone="warn">جذبِ کم — بررسی</Chip>
                        </>
                      ) : r.flag === 'high' ? (
                        <>
                          {' '}
                          <Chip tone="info">جذبِ زیاد</Chip>
                        </>
                      ) : null}
                    </td>
                    <td className="num">{formatPercent(r.revenueShare)}</td>
                    <td className="num">{formatRial(r.avgMaterialPerUnit)}</td>
                    <td className="num">{formatRial(r.avgConversionPerUnit)}</td>
                    <td
                      className="num"
                      style={{ color: r.flag === 'low' ? 'var(--amber)' : undefined }}
                    >
                      {formatPercent(r.conversionShareOfCogs)}
                    </td>
                    <td className="num">
                      {r.fallbackUnitShare > 0 ? (
                        <span style={{ color: 'var(--amber)' }}>
                          {formatPercent(r.fallbackUnitShare)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.8 }}>
            «سهم eqKg تخمینی» = نسبت واحدهایی که وزن سیم‌پیچ ندارند و جذبشان از روی بهای مواد تخمین
            زده می‌شود (کم‌اعتمادترین بخش توزیع). نوعِ «جذبِ کم» جایی است که سهم جذب از میانه بسیار
            پایین‌تر است — اگر آن نوع در واقع دستمزدبر باشد، ممکن است کم‌بها شود.
          </p>
        </>
      )}
    </Card>
  );
}
